import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Stack,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Pending as PendingIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { KYCService, KYC_STATUS } from '../../../utils/kycService';
import { handleKYCError, handleKYCSuccess } from '../../../utils/kycUtils';

// Status Card Component
const StatusCard = ({ title, status, progress, lastUpdated, onRetry, canRetry }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case KYC_STATUS.VERIFIED:
        return {
          color: 'success',
          icon: <CheckCircleIcon />,
          bgColor: '#e8f5e8',
          textColor: '#2e7d32'
        };
      case KYC_STATUS.PROCESSING:
        return {
          color: 'info',
          icon: <ScheduleIcon />,
          bgColor: '#e3f2fd',
          textColor: '#1976d2'
        };
      case KYC_STATUS.REJECTED:
        return {
          color: 'error',
          icon: <ErrorIcon />,
          bgColor: '#ffebee',
          textColor: '#d32f2f'
        };
      case KYC_STATUS.PENDING:
        return {
          color: 'warning',
          icon: <PendingIcon />,
          bgColor: '#fff3e0',
          textColor: '#f57c00'
        };
      default:
        return {
          color: 'default',
          icon: <WarningIcon />,
          bgColor: '#f5f5f5',
          textColor: '#757575'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Card 
      sx={{ 
        height: '100%',
        border: `2px solid ${config.bgColor}`,
        '&:hover': {
          boxShadow: 3
        }
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              backgroundColor: config.bgColor,
              color: config.textColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {config.icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Stack>

        <Chip
          label={status?.toUpperCase() || 'NOT STARTED'}
          color={config.color}
          size="small"
          sx={{ mb: 2 }}
        />

        {progress !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Progress: {progress}%
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              color={config.color}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
        )}

        {lastUpdated && (
          <Typography variant="caption" color="text.secondary">
            Last updated: {new Date(lastUpdated).toLocaleDateString()}
          </Typography>
        )}

        {canRetry && status === KYC_STATUS.REJECTED && (
          <Button
            variant="outlined"
            size="small"
            onClick={onRetry}
            sx={{ mt: 2 }}
            fullWidth
          >
            Retry Verification
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Timeline Component for KYC History
const KYCTimeline = ({ history }) => {
  const getTimelineIcon = (status) => {
    switch (status) {
      case KYC_STATUS.VERIFIED:
        return <CheckCircleIcon color="success" />;
      case KYC_STATUS.REJECTED:
        return <ErrorIcon color="error" />;
      case KYC_STATUS.PROCESSING:
        return <ScheduleIcon color="info" />;
      default:
        return <PendingIcon color="warning" />;
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Verification History
        </Typography>
        <Timeline>
          {history?.map((item, index) => (
            <TimelineItem key={index}>
              <TimelineOppositeContent
                sx={{ m: 'auto 0' }}
                align="right"
                variant="body2"
                color="text.secondary"
              >
                {new Date(item.timestamp).toLocaleString()}
              </TimelineOppositeContent>
              <TimelineSeparator>
                <TimelineConnector />
                <TimelineDot color="grey">
                  {getTimelineIcon(item.status)}
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent sx={{ py: '12px', px: 2 }}>
                <Typography variant="h6" component="span">
                  {item.type} Verification
                </Typography>
                <Typography>{item.message}</Typography>
                {item.reason && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {item.reason}
                  </Alert>
                )}
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      </CardContent>
    </Card>
  );
};

// Overall Progress Component
const OverallProgress = ({ kycData }) => {
  const totalSteps = 3; // PAN, Aadhaar, Bank
  const completedSteps = Object.values(kycData).filter(
    status => status === KYC_STATUS.VERIFIED
  ).length;
  
  const progressPercentage = (completedSteps / totalSteps) * 100;
  
  const getOverallStatus = () => {
    if (completedSteps === totalSteps) return 'All Verified';
    if (completedSteps === 0) return 'Not Started';
    if (Object.values(kycData).some(status => status === KYC_STATUS.REJECTED)) {
      return 'Some Rejected';
    }
    return 'In Progress';
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h5">KYC Progress</Typography>
        <Chip
          label={getOverallStatus()}
          color={completedSteps === totalSteps ? 'success' : 'primary'}
          size="small"
        />
      </Stack>
      
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="body2">
            {completedSteps} of {totalSteps} verifications completed
          </Typography>
          <Typography variant="body2">
            {Math.round(progressPercentage)}%
          </Typography>
        </Stack>
        <LinearProgress 
          variant="determinate" 
          value={progressPercentage} 
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary">
        Complete all verifications to unlock full platform access
      </Typography>
    </Paper>
  );
};

// Main KYC Status Component
const KYCStatus = () => {
  const navigate = useNavigate();
  const [kycData, setKycData] = useState({
    pan: null,
    aadhaar: null,
    bank: null
  });
  const [kycHistory, setKycHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadKYCData();
  }, []);

  const loadKYCData = async () => {
    try {
      setIsLoading(true);
      const userId = sessionStorage.getItem('user_id');
      
      // Load KYC status and history in parallel
      const [statusResponse, historyResponse] = await Promise.all([
        KYCService.getKYCStatus(userId),
        KYCService.getKYCHistory(userId)
      ]);

      if (statusResponse?.data?.status) {
        setKycData(statusResponse.data.data);
      }

      if (historyResponse?.data?.status) {
        setKycHistory(historyResponse.data.data);
      }

      setLastUpdated(new Date());
    } catch (error) {
      handleKYCError(error, 'Loading KYC data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async (type) => {
    try {
      // Navigate to KYC verification page with specific tab
      const tabIndex = type === 'pan' ? 0 : type === 'aadhaar' ? 1 : 2;
      navigate(`/dashboard/kyc-verification?tab=${tabIndex}`);
    } catch (error) {
      handleKYCError(error, 'Retry verification');
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      const response = await KYCService.exportKYCData({}, 'pdf');
      if (response?.data) {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'kyc-certificate.pdf';
        link.click();
        window.URL.revokeObjectURL(url);
        handleKYCSuccess('Certificate downloaded successfully');
      }
    } catch (error) {
      handleKYCError(error, 'Download certificate');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const allVerified = Object.values(kycData).every(status => status === KYC_STATUS.VERIFIED);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">
          KYC Status
        </Typography>
        <Stack direction="row" spacing={2}>
          <Tooltip title="Refresh Status">
            <IconButton onClick={loadKYCData} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {allVerified && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCertificate}
            >
              Download Certificate
            </Button>
          )}
        </Stack>
      </Stack>

      {lastUpdated && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Last updated: {lastUpdated.toLocaleString()}
        </Alert>
      )}

      <OverallProgress kycData={kycData} />

      <Grid container spacing={3}>
        {/* Status Cards */}
        <Grid item xs={12} md={4}>
          <StatusCard
            title="PAN Verification"
            status={kycData.pan}
            progress={kycData.pan === KYC_STATUS.VERIFIED ? 100 : 0}
            lastUpdated={kycData.panLastUpdated}
            onRetry={() => handleRetry('pan')}
            canRetry={true}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatusCard
            title="Aadhaar Verification"
            status={kycData.aadhaar}
            progress={kycData.aadhaar === KYC_STATUS.VERIFIED ? 100 : 0}
            lastUpdated={kycData.aadhaarLastUpdated}
            onRetry={() => handleRetry('aadhaar')}
            canRetry={true}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatusCard
            title="Bank Verification"
            status={kycData.bank}
            progress={kycData.bank === KYC_STATUS.VERIFIED ? 100 : 0}
            lastUpdated={kycData.bankLastUpdated}
            onRetry={() => handleRetry('bank')}
            canRetry={true}
          />
        </Grid>

        {/* Timeline */}
        <Grid item xs={12}>
          <KYCTimeline history={kycHistory} />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard/kyc-verification')}
          disabled={allVerified}
        >
          {allVerified ? 'All Verified' : 'Continue Verification'}
        </Button>
      </Stack>

      {/* Success Message */}
      {allVerified && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="h6">
            🎉 Congratulations! Your KYC verification is complete.
          </Typography>
          <Typography variant="body2">
            You now have full access to all platform features.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default KYCStatus;
